import type { PropsWithChildren } from 'react';
import { isValidElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Collapse } from 'react-collapse';
import { defineMessages, FormattedMessage } from 'react-intl';
import useMeasure from 'react-use/lib/useMeasure';
import { twMerge } from 'tailwind-merge';

import { Button } from '../../components/Button/Button';
import CustomGallery from '../../components/CustomGallery';
import { Chevron } from '../../components/layout/Chevron';
import type { EjsBlockData, EjsBlockImage } from '../../editor/editorjs/@types';
import { parseJSONString } from '../../utils/json';
import type { MessageParsedEmbeds } from '../ThreadMessage/threadTypes';
import { EmbedsOverview } from './EmbedsOverview';
import './MessageLimiter.scss';

const theme = { collapse: 'duration-150', content: 'relative' };
const messages = defineMessages({
  readMore: { defaultMessage: 'Read more', id: 'S++WdB' },
});

// Needed to avoid a TS error when using the editorjs types.
const isEjsBlockImage = (block: EjsBlockData): block is EjsBlockImage => block.type === 'image';

type MessageLimiterProps = PropsWithChildren<{
  limit?: number;
  tolerance?: number;
  onLimiterClick?: () => void;
}>;

export const MessageLimiter = ({
  children,
  limit = 264,
  tolerance = 96,
  onLimiterClick,
}: MessageLimiterProps) => {
  const [showAll, setShowAll] = useState(false);
  const [fullContentHeight, setFullContentHeight] = useState(0);
  const [ref, { height }] = useMeasure<HTMLDivElement>();

  useEffect(() => {
    // Hack to keep track of total content height even if when remove images when collapsed
    if (height > fullContentHeight) {
      setFullContentHeight(height);
    }
  }, [height, fullContentHeight]);

  const messageEmbeds = isValidElement(children)
    ? parseJSONString<MessageParsedEmbeds>(children.props.embeds)
    : null;

  const isImagesOnly = useMemo(
    () => messageEmbeds?.blocks?.every(isEjsBlockImage),
    [messageEmbeds?.blocks],
  );

  const embededImages = useMemo(
    () => messageEmbeds?.blocks.filter(isEjsBlockImage).map(imageBlock => imageBlock.data.file.url),
    [messageEmbeds?.blocks],
  );

  const handleLimiterClick = useCallback(() => {
    if (onLimiterClick) {
      onLimiterClick();
    } else {
      setShowAll(true);
    }
  }, [onLimiterClick, setShowAll]);

  const isLimiting = fullContentHeight > limit && !showAll;

  return (
    <>
      <div className={twMerge('MessageLimiter', isLimiting && 'limited')}>
        <Collapse theme={theme} isOpened>
          <div style={showAll ? undefined : { maxHeight: limit }} className="overflow-hidden">
            <div ref={ref}>{children}</div>
          </div>
          {isLimiting && (
            <div
              className={twMerge(
                'messageCollapser flex flex-col w-full bg-gradient-to-t from-white via-white to-transparentWhite bottom-0 left-0 justify-end',
                !isImagesOnly && 'absolute pt-8',
              )}
              {...(!isImagesOnly && { style: { height: tolerance } })}
            >
              <Button
                className={twMerge('w-full text-center no-underline', !isImagesOnly && 'pt-8')}
                variant="link"
                onClick={handleLimiterClick}
              >
                <div className="flex flex-col items-center justify-center py-2 px-3 rounded-full no-underline">
                  <span className="text-xxs uppercase tracking-[2.2px] text-gray-semiDark leading-4">
                    <FormattedMessage {...messages.readMore} />
                  </span>
                  <div className="transform rotate-90 inline-block text-gray-medium w-4 h-4">
                    <Chevron />
                  </div>
                </div>
              </Button>
            </div>
          )}
        </Collapse>
      </div>
      {isLimiting && !!embededImages?.length && <CustomGallery photos={embededImages} />}
      {isLimiting && messageEmbeds && <EmbedsOverview messageEmbeds={messageEmbeds} />}
    </>
  );
};
